import { SimilarityLevel } from '@/enums'

/**
 * A constant object that contains the default configuration options for similarity calculations. These options are immutable.
 *
 * @constant {Object} DEFAULT_SIMILARITY_OPTIONS
 * @property {number} threshold - The similarity threshold value, typically used to determine whether two items are considered similar.
 * @property {number} verySimilarPercentage - The percentage (of the "similar range" above the threshold) used to define items that are highly similar.
 * @property {number} somewhatSimilarOffset - The offset margin applied to determine items that are somewhat similar.
 */
export const DEFAULT_SIMILARITY_OPTIONS = Object.freeze({
    threshold: 0.85,
    verySimilarPercentage: 50,
    somewhatSimilarOffset: 0.05,
})

/**
 * Determines the similarity level of a given score based on specified thresholds and options.
 *
 * @param {number|string} score The similarity score to evaluate. Can be a numeric value or a numeric string.
 * @param {Object} [options] The configuration options for determining similarity levels.
 * @param {number} [options.threshold=DEFAULT_SIMILARITY_OPTIONS.threshold] The similarity threshold value. Scores above this value are considered similar.
 * @param {number} [options.verySimilarPercentage=DEFAULT_SIMILARITY_OPTIONS.verySimilarPercentage] A percentage to adjust the threshold for categorizing as "very similar".
 * @param {number} [options.somewhatSimilarOffset=DEFAULT_SIMILARITY_OPTIONS.somewhatSimilarOffset] An offset used to determine the lower bound for "somewhat similar".
 * @return {SimilarityLevel|null} Returns a similarity level (IDENTICAL, VERY_SIMILAR, SIMILAR, SOMEWHAT_SIMILAR, DIFFERENT) based on the score, or null if the input score is invalid.
 */
export function getSimilarityLevel(
    score,
    {
        threshold = DEFAULT_SIMILARITY_OPTIONS.threshold,
        verySimilarPercentage = DEFAULT_SIMILARITY_OPTIONS.verySimilarPercentage,
        somewhatSimilarOffset = DEFAULT_SIMILARITY_OPTIONS.somewhatSimilarOffset,
    } = {}
) {
    if (score === null || score === undefined || score === '' || isNaN(score)) { return null }

    // Clamp values to valid ranges
    const validThreshold = Math.max(0, Math.min(1, threshold))
    const validVerySimilarPercentage = Math.max(0, Math.min(100, verySimilarPercentage))
    const validSomewhatSimilarOffset = Math.max(0, Math.min(1, somewhatSimilarOffset))

    const numericScore = Number(score)

    const verySimilarThreshold = 1 - ((1 - validThreshold) * (validVerySimilarPercentage / 100))
    const somewhatSimilarThreshold = validThreshold - validSomewhatSimilarOffset

    if (numericScore >= 1) {
        return SimilarityLevel.IDENTICAL
    } else if (numericScore >= Number(verySimilarThreshold.toFixed(4))) {
        return SimilarityLevel.VERY_SIMILAR
    } else if (numericScore >= Number(validThreshold.toFixed(4))) {
        return SimilarityLevel.SIMILAR
    } else if (numericScore >= Number(somewhatSimilarThreshold.toFixed(4))) {
        return SimilarityLevel.SOMEWHAT_SIMILAR
    }

    return SimilarityLevel.DIFFERENT
}

/**
 * Returns the similarity score as a percentage.
 */
export function formatSimilarityScore (score) {
    return `${(Number(score) * 100).toFixed(2)}%`
}

/**
 * Returns the icon name that corresponds to the given similarity level.
 *
 * @param {SimilarityLevel} similarityLevel - The level of similarity to evaluate.
 * @return {string} The name of the icon matching the specified similarity level.
 */
export function getSimilarityLevelIcon (similarityLevel) {
    switch (similarityLevel) {
        case SimilarityLevel.IDENTICAL:
            return 'mib-check-circle-1'

        case SimilarityLevel.VERY_SIMILAR:
        case SimilarityLevel.SIMILAR:
        case SimilarityLevel.SOMEWHAT_SIMILAR:
            return 'mib-information-circle'

        case SimilarityLevel.DIFFERENT:
            return 'mib-information-circle'

        default:
            return 'mib-information-circle'
    }
}
